import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Slider,
    Typography,
    createStyles,
    makeStyles,
    withStyles,
} from '@material-ui/core'
import Select from '@material-ui/core/Select'
import React, { useImperativeHandle, useRef, forwardRef } from 'react'
import styled from 'styled-components'
import DeleteIcon from 'assets/icons/iconsCad/delete.svg'
import { useTranslation } from 'react-i18next'
import {
    useQueryParams,
    NumberParam,
    StringParam,
    BooleanParam,
    searchStringToObject,
    ArrayParam,
} from 'use-query-params'

export enum FilterTypes {
    NUMBER = 'number',
    BOOLEAN = 'boolean',
    SELECT = 'select',
    RANGE = 'range',
}

type mark = { value: number; label: string }
export interface FiltersProp {
    type: FilterTypes
    value?: number | string | boolean
    label: string
    translation?: string
    items?: string[]
    min?: number
    max?: number
    step?: number
    marks?: mark[]
}

const useStyles = makeStyles(() =>
    createStyles({
        formControl: {
            minWidth: '10px',
        },
        formControlLabel: {
            alignItems: 'start',
            paddingBottom: '4px',
        },
        selectEmpty: {},
    })
)

interface LocationProps {
    location: string
    activeZones?: FiltersProp[]
}

const AirbnbSlider = withStyles({
    root: {
        color: '#3a8589',
        height: 3,
        width: 285,
        marginLeft: 10,
        marginRight: 5,
    },
    thumb: {
        height: 15,
        width: 15,
        backgroundColor: '#fff',
        border: '1px solid currentColor',
        marginTop: -7,
        marginLeft: -7,
        boxShadow: '#ebebeb 0 2px 2px',
        '&:focus, &:hover, &$active': {
            boxShadow: '#ccc 0 2px 3px 1px',
        },
        '& .bar': {
            // display: inline-block !important;
            height: 6,
            width: 1,
            backgroundColor: 'currentColor',
            marginLeft: 1,
            marginRight: 1,
        },
    },
    markLabel: {
        fontSize: 12,
        marginLeft: 10,
    },

    active: {},
    track: {
        height: 3,
    },
    rail: {
        color: '#d8d8d8',
        opacity: 1,
        height: 3,
    },
})(Slider)

function AirbnbThumbComponent(
    props: JSX.IntrinsicAttributes &
        React.ClassAttributes<HTMLSpanElement> &
        React.HTMLAttributes<HTMLSpanElement>
) {
    return (
        <span {...props}>
            <span className="bar" />
            <span className="bar" />
            <span className="bar" />
        </span>
    )
}

const Flex = styled.div`
    display: flex;
    justify-content: space-between;
    button {
        font-size: 10px;
    }
`

const Icon = styled(DeleteIcon)`
    width: 15px;
    height: 15px;
    margin-top: -3px;
    margin-right: -5px;
    fill: #f50057;
`
export const FilterItem = forwardRef(
    (
        {
            type,
            label,
            items,
            max,
            min,
            location,
            marks,
            step,
            activeZones = [],
            translation = '',
        }: FiltersProp & LocationProps,
        ref
    ) => {
        const { t } = useTranslation()
        const classes = useStyles()
        const [, setBoolean] = useQueryParams({ [label]: BooleanParam })
        const [number, setNumber] = useQueryParams({ [label]: NumberParam })
        const [string, setString] = useQueryParams({ [label]: StringParam })
        const [array, setArray] = useQueryParams({ [label]: ArrayParam })

        const inputRef = useRef(null)

        const filters = searchStringToObject(location)
        const filterUsed = filters[label]

        useImperativeHandle(ref, () => ({
            clear() {
                activeZones.length &&
                    activeZones.map((zone) => {
                        const { type: _type, label: l } = zone
                        if (_type === FilterTypes.BOOLEAN) {
                            setBoolean({ [l]: undefined })
                        } else {
                            setString({ [l]: undefined })
                        }
                    })
            },
        }))
        switch (type) {
            case FilterTypes.BOOLEAN:
                return (
                    <div>
                        <FormControlLabel
                            ref={inputRef}
                            id={label}
                            className={classes.formControlLabel}
                            control={
                                <Checkbox
                                    style={{ padding: '0 3px' }}
                                    checked={!!filterUsed}
                                    onChange={() => {
                                        if (filterUsed) {
                                            setBoolean({
                                                [label]: undefined,
                                            })
                                        } else {
                                            setBoolean({
                                                [label]: true,
                                            })
                                        }
                                    }}
                                />
                            }
                            label={
                                translation
                                    ? t(`houses.${translation}`)
                                    : t(`houses.${label}`)
                            }
                        />
                    </div>
                )
            case FilterTypes.NUMBER:
                return (
                    <div>
                        <FormControlLabel
                            ref={inputRef}
                            id={label}
                            control={
                                <Checkbox
                                    style={{ padding: 3 }}
                                    checked={!!filterUsed}
                                    onChange={() => {
                                        if (filterUsed) {
                                            setNumber({
                                                [label]: undefined,
                                            })
                                        } else {
                                            setNumber({
                                                [label]: 1,
                                            })
                                        }
                                    }}
                                />
                            }
                            label={
                                translation
                                    ? t(`houses.${translation}`)
                                    : t(`houses.${label}`)
                            }
                        />
                    </div>
                )
            case FilterTypes.RANGE:
                return (
                    <Box style={{ width: 300 }}>
                        <Flex>
                            <Typography gutterBottom>
                                {`${t(`houses.${label}`)}`}{' '}
                                {array[label] &&
                                (array[label][0] !== min ||
                                    array[label][1] !== max)
                                    ? `${label}` === 'houseArea'
                                        ? `${array[label][0]} - ${array[label][1]} m² `
                                        : `${number[label]} m`
                                    : ''}
                            </Typography>
                            {array[label] &&
                                (array[label][0] !== min ||
                                    array[label][1] !== max) && (
                                    <Button
                                        onClick={() => {
                                            setTimeout(
                                                () =>
                                                    setArray({
                                                        [label]: undefined,
                                                    }),
                                                10
                                            )
                                        }}
                                        size="small"
                                        startIcon={<Icon />}
                                    >
                                        {t(`houses.clear`)}
                                    </Button>
                                )}
                        </Flex>
                        <AirbnbSlider
                            ThumbComponent={AirbnbThumbComponent}
                            step={step}
                            marks={marks}
                            value={
                                array[label]
                                    ? [array[label][0], array[label][1]]
                                    : [min, max]
                            }
                            min={min}
                            max={max}
                            onChange={(_, val) =>
                                setArray({
                                    [label]:
                                        typeof val === 'number'
                                            ? [val, val]
                                            : val,
                                })
                            }
                        />
                    </Box>
                )
            case FilterTypes.SELECT:
                return (
                    <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.formControl}
                    >
                        <InputLabel
                            id={label}
                            style={{
                                backgroundColor: 'white',
                                padding: '0 3px',
                                width: 'fit-content',
                            }}
                        >
                            {translation
                                ? t(`houses.${translation}`)
                                : t(`houses.${label}`)}
                        </InputLabel>
                        <Select
                            labelId={label}
                            id={label + 1}
                            value={string[label] || ''}
                            onChange={(e) =>
                                setString({
                                    [label]:
                                        (e.target?.value as string) == ''
                                            ? undefined
                                            : (e.target.value as string),
                                })
                            }
                        >
                            <MenuItem value={''}>
                                <em>{t(`houses.none`)}</em>
                            </MenuItem>
                            {items?.map((item: string, i: number) => (
                                <MenuItem key={item + i} value={item}>
                                    {t(`houses.${item}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )
            default:
                return null
        }
    }
)
